import React, {
  ChangeEvent,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { PhoneInput } from '../../components';
import { useTranslation } from '../../translations';
import { ordinalSuffix } from '../../utils';
import { FormActionType, FormStateType } from '../form/FormReducer';

interface EmergencyContactInfoProps {
  readonly idx: number;
  readonly name: string | null | undefined;
  readonly phone: string | null | undefined;
  readonly setName: Dispatch<SetStateAction<string | null | undefined>>;
  readonly setPhone: Dispatch<SetStateAction<string | null | undefined>>;
  readonly required?: boolean;
}

const EmergencyContactInfo: FC<EmergencyContactInfoProps> = ({
  idx,
  name,
  phone,
  setName,
  setPhone,
  required,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const { ts } = useTranslation();
  const elName = `emergencyContactName${idx}`;
  const elPhone = `emergencyContactPhone${idx}`;
  const ordSuffix = ordinalSuffix(idx);
  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      // TODO: Should this use the auto-nulling state?
      setName(e.target.value);
    },
    [setName],
  );

  useEffect(() => {
    if (ref.current) {
      if (name || !required) {
        ref.current.setCustomValidity('');
      } else {
        ref.current.setCustomValidity(ts('emergency_contact_info.required'));
      }
    }
  }, [name, required, ts]);

  return (
    <Row>
      <Col md={6} xs={12}>
        <FormGroup>
          <Label for={elName}>
            {ordSuffix} {ts('emergency_contact_info.contact_name')}
            {required ? ` (${ts('common.required')})` : ''}
          </Label>
          <Input id={elName} innerRef={ref} name={elName} onChange={onChange} value={name ?? ''} />
        </FormGroup>
      </Col>
      <Col md={6} xs={12}>
        <FormGroup>
          <Label for={elPhone}>
            {ordSuffix} {ts('emergency_contact_info.contact_phone')}
          </Label>
          <PhoneInput id={elPhone} onChange={setPhone} value={phone} />
        </FormGroup>
      </Col>
    </Row>
  );
};

export const EmergencyContactDynamic: FC<{
  readonly required?: boolean;
  readonly state: FormStateType;
  readonly dispatch: Dispatch<FormActionType>;
}> = ({ state, dispatch, required }) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (state.type !== 'registration') {
    return null;
  }

  return (
    <>
      <EmergencyContactInfo
        idx={1}
        name={state.emergencyContactName1}
        phone={state.emergencyContactPhone1}
        required={required}
        setName={(v) => {
          dispatch({ type: 'setEmergencyContact', name1: v as string });
        }}
        setPhone={(v) => {
          dispatch({ type: 'setEmergencyContact', phone1: v as string });
        }}
      />
      <EmergencyContactInfo
        idx={2}
        name={state.emergencyContactName2}
        phone={state.emergencyContactPhone2}
        setName={(v) => {
          dispatch({ type: 'setEmergencyContact', name2: v as string });
        }}
        setPhone={(v) => {
          dispatch({ type: 'setEmergencyContact', phone2: v as string });
        }}
      />
    </>
  );
};
